import {getTestProps} from '@benchling/js-test-utils';
import {Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ActionOrigin, MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  getModelIconUrl,
  requireScientificModel,
  type ScientificModelNode,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {FONT_PROXIMA_14, FONT_PROXIMA_15, FONT_PROXIMA_17} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {
  GRAY_DARKER,
  GRAY_LIGHT,
  GRAY_LIGHTER,
  ORANGE_LIGHTEST,
  WHITE,
  BLUE,
  ORANGE,
  BLUE_POWDER_LIGHT,
} from 'ui-library/Colors';
import {LocalTime} from 'ui-library/Times';

import {AvailableStructureInfo} from '../AlphaFoldTabUtils';

export interface InProgressPredictionData {
  id: string;
  model: ScientificModelNode;
  createdAt: string;
  componentIds: Array<string>;
}

export function MetadataItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string | JSX.Element;
  className?: object;
}): JSX.Element {
  return (
    <div className={css(styles.metadataItem, className)}>
      <span className={css(styles.metadataLabel)}>{label}</span>
      <span className={css(styles.metadataValue)}>{value}</span>
    </div>
  );
}

function MultimerComponents({componentIds}: {componentIds: Array<string>}): JSX.Element | null {
  return (
    <span
      className={css(styles.componentsList)}
      // Prevent expanding the chip list or clicking on a chip from selecting the prediction
      // (which navigates to the prediction)
      onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
        e.stopPropagation();
      }}
    >
      <ChipList itemIds={componentIds} chipSize={ChipSize.MINI} maxChips={1} isVertical={false} />
    </span>
  );
}

function MetadataRow({
  model,
  version,
  date,
  componentIds,
}: {
  model: ScientificModelNode;
  version?: string;
  date: string;
  componentIds: Array<string>;
}): JSX.Element {
  return (
    <div className={css(styles.metadataRow)}>
      <MetadataItem label="Model" value={model.displayName} className={styles.modelItem} />
      {version && <MetadataItem label="Version" value={version} className={styles.versionItem} />}
      <MetadataItem label="Date" value={<LocalTime datetime={date} />} className={styles.dateItem} />
      {componentIds.length > 1 && (
        <MetadataItem
          label="Multimer Components"
          value={<MultimerComponents componentIds={componentIds} />}
        />
      )}
    </div>
  );
}

function CoordinateStructureListItem({
  coordinateStructure,
  model,
  onClick,
}: {
  coordinateStructure: AvailableStructureInfo;
  model: ScientificModelNode;
  onClick: () => void;
}): JSX.Element {
  return (
    <div
      className={css(styles.coordinateStructureItem, styles.clickableCoordinateStructureItem)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      {...getTestProps({
        component: 'CoordinateStructureList',
        element: 'finalizedItem',
        key: coordinateStructure.structureFile.id,
      })}
      data-amp-safe={false}
    >
      <div className={css(styles.coordinateStructureIcon)}>
        <div className={css(styles.iconCircle)}>
          <img
            src={getModelIconUrl(model)}
            alt={`${model.displayName} icon`}
            className={css(styles.modelIcon)}
          />
        </div>
      </div>
      <div className={css(styles.coordinateStructureContent)}>
        <MetadataRow
          model={model}
          version={coordinateStructure.modelVersion}
          date={coordinateStructure.createdAt}
          componentIds={coordinateStructure.componentIds}
        />
      </div>
    </div>
  );
}

function InProgressPredictionItem({
  structurePredictionRun,
}: {
  structurePredictionRun: InProgressPredictionData;
}): JSX.Element {
  return (
    <div
      className={css(styles.coordinateStructureItem)}
      {...getTestProps({
        component: 'CoordinateStructureList',
        element: 'inProgressItem',
        key: structurePredictionRun.id,
      })}
      data-amp-safe={false}
    >
      <div className={css(styles.coordinateStructureIcon)}>
        <div className={css(styles.iconCircle, styles.inProgressIcon)}>
          <div className={css(styles.spinnerIcon)} />
        </div>
      </div>
      <div className={css(styles.coordinateStructureContent)}>
        <MetadataRow
          model={structurePredictionRun.model}
          date={structurePredictionRun.createdAt}
          componentIds={structurePredictionRun.componentIds}
        />
      </div>
    </div>
  );
}

export function CoordinateStructuresList({
  coordinateStructures,
  catalogByExternalReference,
  onSelectCoordinateStructure,
  onCreateRun,
  onRunAllModels,
  inProgressStructurePredictions,
  disabledMessage,
  runAllModelsDisabled = false,
}: {
  coordinateStructures: Array<AvailableStructureInfo>;
  catalogByExternalReference: ScientificModelsByExternalReference;
  onSelectCoordinateStructure: (coordinateStructure: AvailableStructureInfo) => void;
  onCreateRun: () => void;
  onRunAllModels: () => Promise<boolean>;
  inProgressStructurePredictions: Array<InProgressPredictionData>;
  disabledMessage?: string;
  // Disables only the "Run all models" button (e.g. while the installed-models query is loading),
  // without disabling "Create run" or showing the warning banner.
  runAllModelsDisabled?: boolean;
}): JSX.Element {
  const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);

  const sortedInProgressPredictions = useMemo(
    () =>
      inProgressStructurePredictions
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [inProgressStructurePredictions]
  );

  return (
    <div
      className={css(styles.coordinateStructuresListWrapper)}
      {...getTestProps({component: 'CoordinateStructureList', element: 'root'})}
    >
      <div className={css(styles.coordinateStructuresContent)}>
        <div className={css(styles.coordinateStructuresHeader)}>
          <h2 className={css(styles.coordinateStructuresTitle)}>Predict 3D structure</h2>
          <div className={css(styles.headerButtons)}>
            <Tooltip label={disabledMessage} disabled={!disabledMessage} withinPortal={true}>
              {/* Span wrapper lets the tooltip trigger on hover even when the button is disabled. */}
              <span>
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  icon="sparkle"
                  size={ButtonSize.MEDIUM}
                  text={awaitingResponse ? 'Submitting...' : 'Run all models'}
                  disabled={Boolean(disabledMessage) || awaitingResponse || runAllModelsDisabled}
                  onClick={async () => {
                    if (awaitingResponse) {
                      return;
                    }
                    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.CLICKED_STRUCTURE_PREDICTION_RUN_ALL_MODELS, {
                      actionOrigin: ActionOrigin.MODEL_HUB_STRUCTURE_PREDICTION_PREDICTION_LIST_PAGE,
                    });
                    setAwaitingResponse(true);
                    try {
                      await onRunAllModels().catch(() => {
                        // AlphaFoldTab surfaces run-all submission errors before rejecting.
                      });
                    } finally {
                      setAwaitingResponse(false);
                    }
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip label={disabledMessage} disabled={!disabledMessage} withinPortal={true}>
              {/* Span wrapper lets the tooltip trigger on hover even when the button is disabled. */}
              <span>
                <Button
                  buttonStyle={ButtonStyle.PRIMARY}
                  icon="sparkle"
                  size={ButtonSize.MEDIUM}
                  text="Create run"
                  disabled={Boolean(disabledMessage)}
                  onClick={onCreateRun}
                  {...getTestProps({component: 'CoordinateStructureList', element: 'createRunButton'})}
                />
              </span>
            </Tooltip>
          </div>
        </div>
        {disabledMessage && <InlineBanner text={disabledMessage} bannerType={InlineBannerType.WARNING} />}

        <h3
          className={css(styles.sectionTitle)}
          {...getTestProps({component: 'CoordinateStructureList', element: 'inProgressSection'})}
        >
          In progress predictions
        </h3>
        {inProgressStructurePredictions.length > 0 ? (
          sortedInProgressPredictions.map((structurePredictionRun) => (
            <InProgressPredictionItem
              key={structurePredictionRun.id}
              structurePredictionRun={structurePredictionRun}
            />
          ))
        ) : (
          <div className={css(styles.coordinateStructureItem)}>No predictions in progress.</div>
        )}

        <h3
          className={css(styles.sectionTitle, styles.finalizedPredictionsTitle)}
          {...getTestProps({component: 'CoordinateStructureList', element: 'finalizedPredictionsSection'})}
        >
          Finalized predictions
        </h3>
        {coordinateStructures.length > 0 ? (
          coordinateStructures.map((coordinateStructure) => (
            <CoordinateStructureListItem
              key={coordinateStructure.structureFile.id}
              coordinateStructure={coordinateStructure}
              model={requireScientificModel(catalogByExternalReference, coordinateStructure.model)}
              onClick={() => {
                onSelectCoordinateStructure(coordinateStructure);
              }}
            />
          ))
        ) : (
          <div className={css(styles.coordinateStructureItem)}>No finalized predictions.</div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  coordinateStructuresListWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  coordinateStructuresContent: {
    flex: '1 1 auto',
    padding: '20px',
    overflowY: 'auto',
  },
  coordinateStructuresHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  headerButtons: {
    display: 'flex',
    gap: '8px',
  },
  coordinateStructuresTitle: {
    ...FONT_PROXIMA_17,
    fontWeight: 'bold',
    margin: '0',
  },
  sectionTitle: {
    ...FONT_PROXIMA_15,
    fontWeight: 'bold',
    margin: '0 0 16px 0',
  },
  coordinateStructureItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '8px',
    border: `1px solid ${GRAY_LIGHT}`,
    marginBottom: '8px',
    backgroundColor: WHITE,
  },
  clickableCoordinateStructureItem: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
      borderColor: GRAY_LIGHT,
    },
    ':focus': {
      outline: `2px solid ${BLUE}`,
      outlineOffset: '2px',
    },
  },
  coordinateStructureIcon: {
    marginRight: '12px',
  },
  iconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: BLUE_POWDER_LIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inProgressIcon: {
    backgroundColor: ORANGE_LIGHTEST,
  },
  coordinateStructureContent: {
    flex: '1 1 auto',
  },
  metadataRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    marginLeft: '16px',
  },
  metadataItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
  },
  metadataLabel: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
    fontWeight: 'bold',
  },
  metadataValue: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
  },
  rightSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
  componentsSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  modelIcon: {
    width: '20px',
    height: '20px',
    objectFit: 'contain',
  },
  spinnerIcon: {
    width: '20px',
    height: '20px',
    border: `2px solid ${ORANGE}`,
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  componentsList: {
    display: 'flex',
    flexDirection: 'row',
  },
  modelItem: {
    minWidth: '45px',
  },
  versionItem: {
    minWidth: '45px',
  },
  dateItem: {
    minWidth: '130px',
  },
  finalizedPredictionsTitle: {
    marginTop: '32px',
  },
});
